import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNavigation from '../../components/feature/BottomNavigation';
import RemedyService, { type Remedy } from '../../services/remedyService';
import { GiSpiralBottle } from 'react-icons/gi';
import { IoDiamondOutline } from 'react-icons/io5';
import { FaPills } from 'react-icons/fa6';

// SVG placeholder inline para evitar CSP violation
const REMEDY_IMAGE_FALLBACK = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"%3E%3Crect fill="%23f3f4f6" width="300" height="200"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-family="system-ui" font-size="48"%3E🌿%3C/text%3E%3C/svg%3E';

export default function RemediesLibraryPage() {
  const navigate = useNavigate();
  const remedyService = RemedyService.getInstance();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedRemedy, setSelectedRemedy] = useState<Remedy | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [remedies, setRemedies] = useState<Remedy[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});

  // Carregar remédios do banco de dados
  useEffect(() => {
    loadRemedies();
    loadCategoryCounts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRemedies = async () => {
    setLoading(true);
    try {
      const data = await remedyService.getAllRemedies();
      setRemedies(data);
    } catch (error) {
      console.error('Erro ao carregar remédios:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryCounts = async () => {
    const counts: Record<string, number> = {};
    const categories = ['all', 'plant', 'mineral', 'animal', 'nosode', 'sarcodio', 'autotherapeutic', 'isotherapeutic'];
    
    for (const category of categories) {
      counts[category] = await remedyService.getCategoryCount(category);
    }
    
    setCategoryCounts(counts);
  };

  const categories = [
    { key: 'all', label: 'Todos', icon: 'ri-medicine-bottle-line', count: categoryCounts['all'] || 0 },
    { key: 'plant', label: 'Vegetais', icon: 'ri-leaf-line', count: categoryCounts['plant'] || 0 },
    { key: 'mineral', label: 'Minerais', icon: 'ri-stone-line', count: categoryCounts['mineral'] || 0 },
    { key: 'animal', label: 'Animais', icon: 'ri-bug-line', count: categoryCounts['animal'] || 0 },
    { key: 'nosode', label: 'Nosódios', icon: 'ri-flask-line', count: categoryCounts['nosode'] || 0 },
    { key: 'sarcodio', label: 'Sarcódio', icon: 'ri-heart-pulse-line', count: categoryCounts['sarcodio'] || 0 },
    { key: 'autotherapeutic', label: 'Tautoterápicos', icon: 'ri-user-heart-line', count: categoryCounts['autotherapeutic'] || 0 },
    { key: 'isotherapeutic', label: 'Isoterápicos', icon: 'ri-focus-3-line', count: categoryCounts['isotherapeutic'] || 0 }
  ];

  const filteredRemedies = remedies.filter(remedy => {
    const matchesSearch = remedy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         remedy.scientificName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         remedy.commonNames.some(name => name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         remedy.mainIndications.some(indication => indication.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || remedy.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'plant': return 'bg-green-100 text-green-600';
      case 'mineral': return 'bg-blue-100 text-blue-600';
      case 'animal': return 'bg-orange-100 text-orange-600';
      case 'nosode': return 'bg-purple-100 text-purple-600';
      case 'sarcodio': return 'bg-pink-100 text-pink-600';
      case 'autotherapeutic': return 'bg-indigo-100 text-indigo-600';
      case 'isotherapeutic': return 'bg-cyan-100 text-cyan-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'plant': return 'ri-leaf-line';
      case 'mineral': return 'ri-stone-line';
      case 'animal': return 'ri-bug-line';
      case 'nosode': return 'ri-flask-line';
      case 'sarcodio': return 'ri-heart-pulse-line';
      case 'autotherapeutic': return 'ri-user-heart-line';
      case 'isotherapeutic': return 'ri-focus-3-line';
      default: return 'ri-medicine-bottle-line';
    }
  };

  const handleRemedyClick = (remedy: Remedy) => {
    setSelectedRemedy(remedy);
    setShowDetails(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 pb-24 font-sans">
      {/* Header */}
      <div 
        className="px-6 py-12 pt-16 pb-16 rounded-b-3xl shadow-lg relative overflow-hidden min-h-[280px]"
        style={{
          backgroundImage: 'url("../../Imagens/estante.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Overlay para melhorar a legibilidade do texto */}
        <div className="absolute inset-0 bg-black/30 rounded-b-3xl"></div>
        
        <div className="relative z-10 h-full flex flex-col justify-between">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm"
            >
              <i className="ri-arrow-left-line text-white text-xl"></i>
            </button>
            <h1 className="text-2xl font-bold text-white" style={{
              textShadow: '2px 4px 8px rgba(0, 0, 0, 0.6), 0px 2px 4px rgba(0, 0, 0, 0.3)'
            }}>Biblioteca de Remédios</h1>
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <i className="ri-book-line text-white text-xl"></i>
            </div>
          </div>

          <div className="bg-white/20 backdrop-blur-sm rounded-3xl p-8 mt-auto">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white mb-3">Homeopatia</h2>
              <p className="text-emerald-100 text-base">Conheça os remédios homeopáticos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Busca */}
      <div className="px-6 py-6">
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar remédio, indicação ou sintoma..."
            className="w-full py-4 pl-12 pr-4 bg-white rounded-2xl border border-gray-200 shadow-lg text-sm focus:border-emerald-300 focus:outline-none"
          />
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
            <i className="ri-search-line text-gray-400 text-xl"></i>
          </div>
        </div>
      </div>

      {/* Categorias */}
      <div className="px-6 mb-6">
        <div className="flex gap-3 overflow-x-auto pb-2">
          {categories.map((category) => (
            <button
              key={category.key}
              onClick={() => setSelectedCategory(category.key)}
              className={`flex-shrink-0 px-4 py-3 rounded-2xl transition-all ${
                selectedCategory === category.key
                  ? 'bg-emerald-500 text-white shadow-lg'
                  : 'bg-white text-gray-600 border border-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                {category.key === 'all' ? (
                  <GiSpiralBottle className="text-lg" />
                ) : category.key === 'mineral' ? (
                  <IoDiamondOutline className="text-lg" />
                ) : category.key === 'autotherapeutic' ? (
                  <FaPills className="text-lg" />
                ) : (
                  <i className={`${category.icon} text-lg`}></i>
                )}
                <span className="text-sm font-medium">{category.label}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  selectedCategory === category.key
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {category.count}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lista de Remédios */}
      <div className="px-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
          </div>
        ) : filteredRemedies.length === 0 ? (
          <div className="text-center py-12">
            <GiSpiralBottle className="mx-auto block text-6xl text-gray-300 mb-4" />
            <p className="text-gray-500">Nenhum remédio encontrado</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRemedies.map((remedy) => (
            <div
              key={remedy.id}
              onClick={() => handleRemedyClick(remedy)}
              className="bg-white rounded-3xl p-6 shadow-xl cursor-pointer hover:scale-105 transition-transform overflow-hidden"
            >
              <div className="flex items-start gap-4 overflow-hidden">
                <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gray-100 flex-shrink-0">
                  <img
                    src={(remedy.image && remedy.image.trim() !== '') ? remedy.image : REMEDY_IMAGE_FALLBACK}
                    alt={remedy.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Evitar loop infinito: só trocar se não for já o fallback
                      if (e.currentTarget.src !== REMEDY_IMAGE_FALLBACK) {
                        e.currentTarget.src = REMEDY_IMAGE_FALLBACK;
                      }
                    }}
                  />
                </div>
                
                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-800 text-lg truncate">{remedy.name}</h3>
                      <p className="text-sm text-gray-600 italic truncate">{remedy.scientificName}</p>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 inline-flex items-center whitespace-nowrap ${getCategoryColor(remedy.category)}`}>
                      <span>
                        {remedy.category === 'plant' ? 'Vegetal' : 
                         remedy.category === 'mineral' ? 'Mineral' : 
                         remedy.category === 'animal' ? 'Animal' : 
                         remedy.category === 'nosode' ? 'Nosódio' :
                         remedy.category === 'sarcodio' ? 'Sarcódio' :
                         remedy.category === 'autotherapeutic' ? 'Tautoterápico' :
                         remedy.category === 'isotherapeutic' ? 'Isoterápico' : 'Outro'}
                      </span>
                    {remedy.category === 'mineral' ? (
                        <IoDiamondOutline className="ml-1 align-middle" />
                      ) : remedy.category === 'autotherapeutic' ? (
                        <FaPills className="ml-1 align-middle" />
                      ) : getCategoryIcon(remedy.category) === 'ri-medicine-bottle-line' ? (
                        <GiSpiralBottle className="ml-1 align-middle" />
                      ) : (
                        <i className={`${getCategoryIcon(remedy.category)} ml-1`}></i>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-700 mb-3 line-clamp-2">{remedy.description}</p>
                  
                  <div className="mb-3">
                    <p className="text-xs font-medium text-gray-600 mb-1">Principais indicações:</p>
                    <div className="flex flex-wrap gap-1">
                      {remedy.mainIndications.slice(0, 3).map((indication, index) => (
                        <span
                          key={index}
                          className="text-xs bg-emerald-100 text-emerald-600 px-2 py-1 rounded-full"
                        >
                          {indication}
                        </span>
                      ))}
                      {remedy.mainIndications.length > 3 && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">
                          +{remedy.mainIndications.length - 3} mais
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Removed potencies display section */}
                    </div>
                    <button className="text-emerald-600 text-sm font-medium flex items-center">
                      Ver detalhes
                      <i className="ri-arrow-right-line ml-1"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          </div>
        )}
      </div>

      {/* Modal de Detalhes do Remédio */}
      {showDetails && selectedRemedy && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 font-sans">
          <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto font-sans">
            <div className="sticky top-0 bg-white p-6 border-b border-gray-100 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800">{selectedRemedy.name}</h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
                >
                  <i className="ri-close-line text-gray-600"></i>
                </button>
              </div>
              <p className="text-sm text-gray-600 italic mt-1">{selectedRemedy.scientificName}</p>
            </div>

            <div className="p-6 space-y-6">
              {/* Imagem */}
              <div className="w-full h-48 rounded-2xl overflow-hidden bg-gray-100">
                <img
                  src={(selectedRemedy.image && selectedRemedy.image.trim() !== '') ? selectedRemedy.image : REMEDY_IMAGE_FALLBACK}
                  alt={selectedRemedy.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Evitar loop infinito: só trocar se não for já o fallback
                    if (e.currentTarget.src !== REMEDY_IMAGE_FALLBACK) {
                      e.currentTarget.src = REMEDY_IMAGE_FALLBACK;
                    }
                  }}
                />
              </div>

              {/* Categoria */}
              <div className="flex items-center justify-center">
                <div className={`px-4 py-2 rounded-full text-sm font-medium inline-flex items-center ${getCategoryColor(selectedRemedy.category)}`}>
                  <span>
                    {selectedRemedy.category === 'plant' ? 'Remédio Vegetal' : 
                     selectedRemedy.category === 'mineral' ? 'Remédio Mineral' : 
                     selectedRemedy.category === 'animal' ? 'Remédio Animal' : 
                     selectedRemedy.category === 'nosode' ? 'Nosódio' :
                     selectedRemedy.category === 'sarcodio' ? 'Sarcódio' :
                     selectedRemedy.category === 'autotherapeutic' ? 'Tautoterápico' :
                     selectedRemedy.category === 'isotherapeutic' ? 'Isoterápico' : 'Outro'}
                  </span>
                  {selectedRemedy.category === 'mineral' ? (
                    <IoDiamondOutline className="ml-2 align-middle" />
                  ) : selectedRemedy.category === 'autotherapeutic' ? (
                    <FaPills className="ml-2 align-middle" />
                  ) : getCategoryIcon(selectedRemedy.category) === 'ri-medicine-bottle-line' ? (
                    <GiSpiralBottle className="ml-2 align-middle" />
                  ) : (
                    <i className={`${getCategoryIcon(selectedRemedy.category)} ml-2`}></i>
                  )}
                </div>
              </div>

              {/* Nomes Comuns */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Nomes Populares</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedRemedy.commonNames.map((name, index) => (
                    <span key={index} className="text-sm bg-blue-100 text-blue-600 px-3 py-1 rounded-full">
                      {name}
                    </span>
                  ))}
                </div>
              </div>

              {/* Descrição */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Descrição</h4>
                <p className="text-sm text-gray-700 leading-relaxed">{selectedRemedy.description}</p>
              </div>

              {/* Constituição */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Constituição</h4>
                <p className="text-sm text-gray-700 leading-relaxed">{selectedRemedy.constitution}</p>
              </div>

              {/* Principais Indicações */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Principais Indicações</h4>
                <div className="space-y-2">
                  {selectedRemedy.mainIndications.map((indication, index) => (
                    <div key={index} className="flex items-start">
                      <i className="ri-check-line text-emerald-500 mt-0.5 mr-2 text-sm"></i>
                      <span className="text-sm text-gray-700">{indication}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sintomas Mentais */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Sintomas Mentais</h4>
                <div className="space-y-2">
                  {selectedRemedy.mentalSymptoms.map((symptom, index) => (
                    <div key={index} className="flex items-start">
                      <i className="ri-brain-line text-purple-500 mt-0.5 mr-2 text-sm"></i>
                      <span className="text-sm text-gray-700">{symptom}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sintomas Físicos */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Sintomas Físicos</h4>
                <div className="space-y-2">
                  {selectedRemedy.physicalSymptoms.map((symptom, index) => (
                    <div key={index} className="flex items-start">
                      <i className="ri-body-scan-line text-blue-500 mt-0.5 mr-2 text-sm"></i>
                      <span className="text-sm text-gray-700">{symptom}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Modalidades */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <i className="ri-arrow-down-line text-red-500 mr-2"></i>
                    Piora com
                  </h4>
                  <div className="space-y-2">
                    {selectedRemedy.modalities.worse.map((item, index) => (
                      <div key={index} className="text-sm bg-red-50 text-red-700 px-3 py-2 rounded-lg">
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <i className="ri-arrow-up-line text-green-500 mr-2"></i>
                    Melhora com
                  </h4>
                  <div className="space-y-2">
                    {selectedRemedy.modalities.better.map((item, index) => (
                      <div key={index} className="text-sm bg-green-50 text-green-700 px-3 py-2 rounded-lg">
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Data de Atualização */}
              <div className="pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-500 text-center">
                  Última atualização: {new Date(selectedRemedy.updated).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white p-6 border-t border-gray-100 rounded-b-3xl">
              <button
                onClick={() => setShowDetails(false)}
                className="w-full py-3 bg-emerald-500 text-white rounded-2xl font-medium"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNavigation />
    </div>
  );
}
